import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid, Paper } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
  RowClassParams,
  RowDoubleClickedEvent,
  RowStyle,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { useEAFProcessesContext } from "../context/EAFProcessesContext";
import { useEAFOptionFunctions } from "../hooks/useEAFOptionFunctions";
import { ProcessByShift } from "../models/ProcessByShift";
import {
  deleteProcess,
  unDeleteProcess,
  unLinkEAFProcess,
} from "../repositories/EAFRepository";
import { LinkEAFProcess } from "./LinkEAFProcess";
import { UpsertEAFProcess } from "./UpsertEAFProcess";
import { CommentModal } from "@/controls/ag-grid/components/CommentModal";
import { useTableData } from "./hooks";
import { EAFProcessByShift } from "@/pages/meltshop/models/ProcessByShift";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { upsertProcessData } from "../repositories/EAFRepository";

type Props = {
  loadingGrid: boolean;
  rows: EAFProcessByShift[];
  refreshData: () => Promise<void>;
};

export const EAFProcessesTable = ({
  loadingGrid,
  rows,
  refreshData,
}: Props) => {
  const gridRef = useRef<AgGridReact>(null);
  const [positionToOpenModal, setPositionToOpenModal] = useState<{
    top: number;
    left: number;
  }>();
  const {
    state: { selectedProcess, errorMessage, processSelected },
    actions: {
      setSelectedProcess,
      setIsLoadingBackground,
      setNotificationMessage,
      setErrorMessage,
    },
  } = useEAFProcessesContext();
  const handleShowCommentModal = (
    show: boolean,
    rowData?: ProcessByShift,
    position?: { top: number; left: number },
  ) => {
    setPositionToOpenModal(position);
    setShowCommentModal(show);
  };
  const {
    createData,
    linkData,
    unlinkData,
    getMenuOptions,
    toggleStatusData,
    goToDataEntry,
    showActiveModal,
    showDeleteModal,
    showEditModal,
    showLinkModal,
    showUnlinkModal,
    showCommentModal,
    messageActiveModal,
    messageDeleteModal,
    messageUnlinkModal,
    setShowActiveModal,
    setShowDeleteModal,
    setShowEditModal,
    setShowLinkModal,
    setShowUnlinkModal,
    setShowCommentModal,
    setMessageActiveModal,
    setMessageDeleteModal,
  } = useEAFOptionFunctions();

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const { columns } = useTableData({
    showCommentModal,
    setShowCommentModal: handleShowCommentModal,
    linkData: linkData,
    unlinkData: unlinkData,
    toggleStatusData: toggleStatusData,
    showContextMenu: showContextMenu,
  });

  const onUnlinkSubmit = async (value?: string) => {
    setShowUnlinkModal(false);
    if (value !== "OK") return;
    await unLinkEAFProcess(selectedProcess!.ProcessId);
    await refreshData();
  };

  const deleteEAFProcess = async (value?: string) => {
    setShowDeleteModal(false);
    setMessageDeleteModal("");
    if (value !== "OK") return;
    if (selectedProcess === null) return;
    setIsLoadingBackground(true);
    const resp = await deleteProcess(selectedProcess.ProcessId);
    if (resp.ok) {
      setIsLoadingBackground(false);
      setNotificationMessage("Process deleted successfully");
      setSelectedProcess(null);
      await refreshData();
    } else {
      setIsLoadingBackground(false);
      setErrorMessage(resp.message);
    }
  };

  const activeEAFProcess = async (value?: string) => {
    setShowActiveModal(false);
    setMessageActiveModal("");
    if (value !== "OK") return;
    if (selectedProcess === null) return;
    setIsLoadingBackground(true);
    const resp = await unDeleteProcess(selectedProcess.ProcessId);
    if (resp.ok) {
      setIsLoadingBackground(false);
      setNotificationMessage("Process activated successfully");
      setSelectedProcess(null);
      await refreshData();
    } else {
      setIsLoadingBackground(false);
      setErrorMessage(resp.message);
    }
  };

  const getContextMenuItems = (
    params: GetContextMenuItemsParams,
  ): (DefaultMenuItem | MenuItemDef)[] => {
    const data = params.node?.data;

    if (data) {
      params.api.deselectAll();
      params.node?.setSelected(true);
      setSelectedProcess(data);
    }

    showContextMenu(
      event as unknown as React.MouseEvent,
      data,
      "eafProcessesContext",
    );

    return [];
  };

  const getRowStyle = (
    params: RowClassParams<EAFProcessByShift>,
  ): RowStyle | undefined => {
    if (params.data?.IsActive) {
      return { background: "#E8EBEB" };
    }
    if (params.data?.ProcessId === processSelected) {
      params.node.setSelected(true);
    }
    return undefined;
  };

  useEffect(() => {
    registerConfig({
      id: "eafProcessesContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <Grid onContextMenu={(e) => e.preventDefault()}>
      <Grid>
        <Grid container>
          <Grid
            component={Paper}
            elevation={3}
            item
            md={12}
            xs={12}
            style={{
              padding: 10,
              height: "calc(100vh - 300px)",
              minHeight: "calc(100vh - 300px)",
            }}
          >
            <ConfirmationDialogRaw
              title="Confirmation"
              message={messageDeleteModal}
              open={showDeleteModal}
              onClose={deleteEAFProcess}
            />
            <ConfirmationDialogRaw
              title="Confirmation"
              message={messageActiveModal}
              open={showActiveModal}
              onClose={activeEAFProcess}
            />
            <ConfirmationDialogRaw
              title="Confirmation"
              message={messageUnlinkModal}
              open={showUnlinkModal}
              onClose={onUnlinkSubmit}
            />
            <UpsertEAFProcess
              show={showEditModal}
              onHide={() => setShowEditModal(false)}
              refreshData={refreshData}
            />
            <LinkEAFProcess
              show={showLinkModal}
              onHide={() => setShowLinkModal(false)}
              refreshData={refreshData}
            />
            <CommentModal
              show={showCommentModal}
              onHide={() => {
                setShowCommentModal(false);
              }}
              refreshData={refreshData}
              position={positionToOpenModal}
              selectedElement={
                selectedProcess
                  ? { ...selectedProcess, ElementId: selectedProcess.ProcessId }
                  : null
              }
              setSelectedElement={setSelectedProcess}
              upsertFunction={upsertProcessData}
            />
            <AgGridReact<EAFProcessByShift>
              ref={gridRef}
              rowData={rows}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              getRowStyle={getRowStyle}
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={(
                event: RowDoubleClickedEvent<EAFProcessByShift>,
              ) => {
                goToDataEntry(event.data?.ProcessId ?? null);
              }}
              onRowClicked={(event) => setSelectedProcess(event.data)}
              loading={loadingGrid}
              {...defaultGridOptions}
              suppressContextMenu={false}
            />
            <Grid container justifyContent="flex-end">
              <Grid
                item
                md={2}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  style={{ margin: "1.5rem 0px 0px" }}
                  onClick={() => {
                    createData();
                  }}
                >
                  NEW PROCESS
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
    </Grid>
  );
};
